import { useRef, useState } from 'react';

/**
 * Radix Select 选中一个选项时，内部会同步调用 onValueChange 后紧跟着调用 onOpenChange(false)，
 * 面板立刻关闭——高亮块的滑动动效完全没有时间播放。
 *
 * 这个 hook 把“选中触发的关闭”和“真正的关闭”（点击外部、Esc）分开处理：前者用
 * pendingCloseRef 标记忽略掉 Radix 那次同步的 onOpenChange(false)，改用定时器延迟
 * closeDelayMs 后才真正关闭，留出时间给滑动动画播完；后者仍然立即生效。
 */
export const useDelayedSelectClose = (onValueChange: (value: string) => void, closeDelayMs = 220) => {
  const [open, setOpen] = useState(false);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const pendingCloseRef = useRef(false);

  const handleValueChange = (value: string) => {
    onValueChange(value);
    pendingCloseRef.current = true;
    clearTimeout(closeTimerRef.current);
    closeTimerRef.current = setTimeout(() => {
      pendingCloseRef.current = false;
      setOpen(false);
    }, closeDelayMs);
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen) {
      pendingCloseRef.current = false;
      clearTimeout(closeTimerRef.current);
      setOpen(true);
      return;
    }

    // 选中触发的那次同步 onOpenChange(false)：忽略，交给上面的定时器延迟关闭。
    if (pendingCloseRef.current) {
      return;
    }

    setOpen(false);
  };

  return { open, onOpenChange: handleOpenChange, onValueChange: handleValueChange };
};
